import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../Common/hooks/useToast";
import { Consultation, MedicineEntry } from "../../../Common/interfaces/doctor.interfaces";
import { useDoctorContext } from "../../../Navigation/context/DoctorContext";
import {
  fetchConsultation,
  fetchPrescription,
  savePrescriptionData,
  uploadPrescriptionDocument,
} from "../https/prescription_module.https";

const DEFAULT_MEDICINE: MedicineEntry = {
  medicine_name: "",
  dosage: "",
  frequency: "",
  duration_days: 1,
  timing: "After Food",
  instructions: "",
};

export const usePrescriptionModule = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const { patientEndedMap } = useDoctorContext();

  useEffect(() => {
    if (id && (patientEndedMap[id] || sessionStorage.getItem(`patientEnded:${id}`) === "true")) {
      showToast("Patient ended the call", "error");
    }
  }, [id, patientEndedMap, showToast]);

  const [consultation, setConsultation] = useState<Consultation | null>(null);
  const [activeTab, setActiveTab] = useState<"digital" | "upload">("digital");
  const [isSaving, setIsSaving] = useState(false);
  const [diagnosis, setDiagnosis] = useState("");
  const [medicines, setMedicines] = useState<MedicineEntry[]>([{ ...DEFAULT_MEDICINE }]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetchConsultation(id)
      .then((r) => { if (r.data?.data) setConsultation(r.data.data); })
      .catch(() => {});
    fetchPrescription(id)
      .then((r) => {
        const data = r.data?.data;
        if (data?.prescription) setDiagnosis(data.prescription.diagnosis || "");
        if (data?.medicines?.length) setMedicines(data.medicines);
      })
      .catch(() => {}); // no existing prescription is fine
  }, [id]);

  const handleAddMedicine = () => setMedicines((prev) => [...prev, { ...DEFAULT_MEDICINE }]);

  const handleRemoveMedicine = (index: number) => {
    if (medicines.length === 1) {
      showToast("At least one medicine is required", "error");
      return;
    }
    setMedicines((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleFieldChange = (index: number, field: keyof MedicineEntry, value: any) => {
    setMedicines((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => setFilePreview(event.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleSave = async () => {
    if (!consultation) return;

    const isEnded = patientEndedMap[id!] || sessionStorage.getItem(`patientEnded:${id}`) === "true";

    if (activeTab === "digital") {
      if (medicines.some((m) => !m.medicine_name.trim() || !m.dosage.trim())) {
        showToast("Please fill in Medicine Name and Dosage for all rows", "error");
        return;
      }
      setIsSaving(true);
      try {
        await savePrescriptionData(id!, {
          beneficiary_id: consultation.beneficiary_id,
          diagnosis,
          medicines: medicines.map((m) => ({ ...m, duration_days: Number(m.duration_days) || 1 })),
        });
        showToast("Prescription generated & saved successfully!", "success");
        setTimeout(() => {
          if (isEnded) {
            navigate(`/consultation/${id}/complete`, { state: { patientEnded: true } });
          } else {
            navigate(-1);
          }
        }, 1500);
      } catch {
        showToast("Failed to save prescription", "error");
      } finally {
        setIsSaving(false);
      }
    } else {
      if (!selectedFile) {
        showToast("Please select a file to upload", "error");
        return;
      }
      setIsSaving(true);
      try {
        await uploadPrescriptionDocument(selectedFile, consultation.beneficiary_id, id!);
        showToast("Prescription file uploaded successfully!", "success");
        setTimeout(() => {
          if (isEnded) {
            navigate(`/consultation/${id}/complete`, { state: { patientEnded: true } });
          } else {
            navigate(-1);
          }
        }, 1500);
      } catch {
        showToast("Failed to upload prescription file", "error");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleBack = () => {
    const isEnded = id && (patientEndedMap[id] || sessionStorage.getItem(`patientEnded:${id}`) === "true");
    if (isEnded) {
      navigate(`/consultation/${id}/complete`, { state: { patientEnded: true } });
    } else {
      navigate(-1);
    }
  };

  return {
    consultation,
    activeTab,
    isSaving,
    diagnosis,
    medicines,
    selectedFile,
    filePreview,
    setActiveTab,
    setDiagnosis,
    setSelectedFile,
    setFilePreview,
    handleAddMedicine,
    handleRemoveMedicine,
    handleFieldChange,
    handleFileChange,
    handleSave,
    handleBack,
    navigate,
    toast,
  };
};
